// ============================================================================
// INQUIRY PITCH DECKS — inquiryPitchDecks.jsx
// ============================================================================
// The pitch decks made for one inquiry, shown in the inquiry's drawer on the
// Inquiries page (docs/pitch-deck-generator-plan.md, section 7.1). Read only:
// status, quality tier, when it was rendered and a link to the PDF, plus a way
// through to the builder in Comms, which is where a deck is actually made.
//
// It lives in the comms folder because comms.pitch_decks and the pitch-decks
// bucket are this section's, not the projects section's, and the styling
// deliberately follows the Inquiries drawer rather than the BRIO comms tabs.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';

const comms = supabase.schema('comms');
const GOLD = 'var(--brio-gold, #9F902A)';
const label = { fontWeight: 500, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', color: GOLD, marginBottom: '6px' };
const note = { fontSize: '11px', color: '#888' };
const QUALITY = { email: 'Email quality', presentation: 'Presentation quality' };

const when = iso => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
const megabytes = bytes => (Number(bytes) > 0 ? `${(Number(bytes) / 1048576).toFixed(1)} MB` : '');

export default function InquiryPitchDecks({ inquiryId, onOpenBuilder }) {
  const [decks, setDecks] = useState(null); // null while loading
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!inquiryId) { setDecks([]); return undefined; }
    let live = true;
    setDecks(null);
    setError(null);
    comms.from('pitch_decks')
      .select('comms_id, title, status, quality, pdf_path, pdf_bytes, rendered_at, updated_at')
      .eq('inquiry_id', inquiryId)
      .order('updated_at', { ascending: false })
      .then(({ data, error: err }) => {
        if (!live) return;
        if (err) { setError(err.message); setDecks([]); return; }
        setDecks(data || []);
      });
    return () => { live = false; };
  }, [inquiryId]);

  // The bucket is private, so a link is signed on the click rather than held
  // in the page: a signed URL printed on render would be stale by the time
  // anybody used it.
  const open = async deck => {
    try {
      const { data, error: err } = await supabase.storage.from('pitch-decks').createSignedUrl(deck.pdf_path, 3600);
      if (err) throw err;
      window.open(data.signedUrl, '_blank', 'noopener');
    } catch (e) { setError(e.message); }
  };

  return (
    <div>
      <div style={label}>Pitch decks</div>
      {error && <p style={{ ...note, color: '#B4402B' }}>{error}</p>}
      {decks === null && <p style={note}>Loading…</p>}
      {decks !== null && decks.length === 0 && <p style={note}>No pitch deck for this inquiry yet.</p>}
      {decks !== null && decks.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
          {decks.map(d => (
            <div key={d.comms_id} style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap', fontSize: '11px', padding: '6px 8px', background: '#fff', border: '1px solid var(--brio-border, #e2e8f0)', borderRadius: '3px' }}>
              <span style={{ fontWeight: 600 }}>{d.title || 'Untitled deck'}</span>
              <span style={{ color: '#888' }}>{d.status || 'draft'}</span>
              <span style={{ color: '#888' }}>{QUALITY[d.quality] || d.quality || ''}</span>
              <span style={{ flex: 1 }} />
              {d.rendered_at
                ? <span style={{ color: '#888' }}>Rendered {when(d.rendered_at)}{megabytes(d.pdf_bytes) ? ` · ${megabytes(d.pdf_bytes)}` : ''}</span>
                : <span style={{ color: '#888' }}>Not rendered yet</span>}
              {d.pdf_path && (
                <button type="button" onClick={() => open(d)} style={{ background: 'none', border: 'none', color: GOLD, cursor: 'pointer', padding: 0, fontSize: '11px', textDecoration: 'underline' }}>Open PDF</button>
              )}
            </div>
          ))}
        </div>
      )}
      {onOpenBuilder && (
        <button type="button" onClick={() => onOpenBuilder(inquiryId)}
          style={{ fontSize: '11px', padding: '5px 10px', background: GOLD, color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>
          {decks && decks.length ? 'Open in Comms' : 'Make a pitch deck'}
        </button>
      )}
    </div>
  );
}
